import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import CountUp from 'react-countup';
import { Play, MagnifyingGlass, CaretUp, YoutubeLogo, InstagramLogo, List, X, Eye } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatViews(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toString();
}

export default function HomePage() {
  const [videos, setVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [shortsVideos, setShortsVideos] = useState([]);
  const [videoOfWeek, setVideoOfWeek] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({ total_videos: 0, total_views: 0, subscriber_count: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchData();
    // Track page visit
    axios.post(`${API}/analytics/track`).catch(() => {});
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchData = async () => {
    try {
      const [videosRes, featuredRes, trendingRes, shortsRes, vowRes, galleryRes, downloadsRes, announcementsRes, settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/videos`),
        axios.get(`${API}/videos/featured`),
        axios.get(`${API}/videos/trending`),
        axios.get(`${API}/videos/shorts`),
        axios.get(`${API}/videos/video-of-week`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/downloads`),
        axios.get(`${API}/announcements`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/stats`)
      ]);

      setVideos(videosRes.data);
      setFeaturedVideos(featuredRes.data);
      setTrendingVideos(trendingRes.data);
      setShortsVideos(shortsRes.data);
      setVideoOfWeek(vowRes.data);
      setGallery(galleryRes.data);
      setDownloads(downloadsRes.data);
      setAnnouncements(announcementsRes.data);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleScroll = () => {
    setShowBackToTop(window.scrollY > 500);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredVideos = videos.filter(v => 
    v.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050810] text-white overflow-x-hidden">
      {/* Announcement Banner */}
      {announcements.length > 0 && (
        <div data-testid="announcement-banner" className="bg-cyan-500/10 border-b border-cyan-500/20 py-2 px-4 text-center">
          <p className="text-sm text-cyan-400">{announcements[0].message}</p>
        </div>
      )}

      {/* Navbar */}
      <nav data-testid="navbar" className="fixed top-0 w-full z-50 bg-[#050810]/80 backdrop-blur-xl border-b border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_54e86be3-66d7-490a-987a-815c7d64dfa5/artifacts/5w1lgswe_channels4_profile.jpg" 
              alt="Slaynx Logo" 
              className="w-12 h-12 rounded-full border-2 border-cyan-500"
            />
            <span className="text-2xl font-black font-['Unbounded'] text-white hero-glow">SLAYNX</span>
          </div>
          <div className="hidden md:flex gap-6">
            <a href="#home" className="text-slate-300 hover:text-cyan-400 font-medium">Home</a>
            <a href="#videos" className="text-slate-300 hover:text-cyan-400 font-medium">Videos</a>
            <a href="#about" className="text-slate-300 hover:text-cyan-400 font-medium">About</a>
            <a href="#gallery" className="text-slate-300 hover:text-cyan-400 font-medium">Gallery</a>
            <a href="#downloads" className="text-slate-300 hover:text-cyan-400 font-medium">Downloads</a>
            <a href="#contact" className="text-slate-300 hover:text-cyan-400 font-medium">Contact</a>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-cyan-400"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#050810]/95 backdrop-blur-xl border-t border-cyan-500/10"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              <a href="#home" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">Home</a>
              <a href="#videos" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">Videos</a>
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">About</a>
              <a href="#gallery" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">Gallery</a>
              <a href="#downloads" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">Downloads</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-cyan-400 font-medium">Contact</a>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_slaynx-nexus/artifacts/w9j6d7w8_channels4_banner.jpg)' }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050810]/60 via-[#050810]/40 to-[#050810]"></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center max-w-4xl px-6"
        >
          <motion.img 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1 }}
            src="https://customer-assets.emergentagent.com/job_54e86be3-66d7-490a-987a-815c7d64dfa5/artifacts/5w1lgswe_channels4_profile.jpg"
            alt="Slaynx" 
            className="w-40 h-40 mx-auto rounded-full border-4 border-cyan-500 mb-8 shadow-[0_0_40px_rgba(0,240,255,0.5)]"
          />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black font-['Unbounded'] mb-6 hero-glow tracking-tighter">
            SLAYNX
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-8 font-['Outfit']">
            Premium Content Creator | Video Editor | Coding Enthusiast
          </p>
          {(
            <motion.a
              data-testid="subscribe-button"
              href="https://youtube.com/@Slaynx"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 bg-cyan-500 text-black font-bold px-8 py-4 rounded-md uppercase tracking-wide shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:bg-cyan-400"
            >
              <YoutubeLogo size={24} weight="fill" />
              Subscribe on YouTube
            </motion.a>
          )}
        </motion.div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </section>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="relative max-w-xl mx-auto">
          <input
            data-testid="search-input"
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#0F1528]/60 backdrop-blur-xl border border-cyan-500/20 rounded-lg px-12 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500/50"
          />
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400" size={20} weight="bold" />
        </div>
      </div>

      {/* Video of the Week */}
      {videoOfWeek && videoOfWeek.id && (
        <section data-testid="video-of-week-section" className="py-16 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Featured This Week</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Video of the Week</h2>
            </div>
            <div className="card-glass rounded-xl p-6 sm:p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <a href={videoOfWeek.url} target="_blank" rel="noopener noreferrer" className="relative group overflow-hidden rounded-lg cursor-pointer block">
                  <img 
                    src={videoOfWeek.thumbnail_url || 'https://images.unsplash.com/photo-1550602883-4c2d2c705db2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsdWUlMjBuZW9uJTIwZ2xhc3N8ZW58MHx8fHwxNzgwNDA5OTg1fDA&ixlib=rb-4.0&q=85'}
                    alt={videoOfWeek.title}
                    className="w-full aspect-video object-cover"
                    onError={(e) => {
                      if (e.target.src.includes('maxresdefault')) {
                        e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Play size={48} weight="fill" className="text-cyan-400" />
                  </div>
                </a>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">{videoOfWeek.title}</h3>
                  <p className="text-slate-300 mb-4">{videoOfWeek.description}</p>
                  <div className="text-sm text-cyan-400 mb-6 inline-flex items-center gap-2">
                    <Eye size={18} weight="duotone" />
                    {formatViews(videoOfWeek.view_count)} views
                  </div>
                  <div>
                    <button
                      data-testid={`watch-video-${videoOfWeek.id}`}
                      onClick={() => setPlayingVideo(videoOfWeek)}
                      className="inline-flex items-center gap-2 bg-cyan-500 text-black font-bold px-6 py-3 rounded-md uppercase tracking-wide hover:bg-cyan-400"
                    >
                      <Play size={20} weight="fill" />
                      Watch Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* Featured Videos */}
      {featuredVideos.length > 0 && (
        <section id="videos" data-testid="featured-videos-section" className="py-16 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Handpicked</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Featured Videos</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} video={video} onPlay={setPlayingVideo} />
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Latest Uploads */}
      {filteredVideos.length > 0 && (
        <section data-testid="latest-videos-section" className="py-16 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Fresh Content</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Latest Uploads</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.slice(0, 6).map((video) => (
                <VideoCard key={video.id} video={video} onPlay={setPlayingVideo} />
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Trending Videos */}
      {trendingVideos.length > 0 && (
        <section data-testid="trending-videos-section" className="py-16 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">What's Hot</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Trending Now</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trendingVideos.map((video) => (
                <VideoCard key={video.id} video={video} onPlay={setPlayingVideo} />
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Shorts Section */}
      {shortsVideos.length > 0 && (
        <section data-testid="shorts-section" className="py-16 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Quick Bites</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">YouTube Shorts</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {shortsVideos.map((video) => (
                <ShortsCard key={video.id} video={video} onPlay={setPlayingVideo} />
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* About Section */}
      <section id="about" data-testid="about-section" className="py-24 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <div className="relative overflow-hidden rounded-xl">
            <img 
              src="https://images.pexels.com/photos/8100060/pexels-photo-8100060.jpeg"
              alt="About"
              className="w-full aspect-video object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050810] to-transparent"></div>
          </div>
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">About Me</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2 mb-6">Creator & Editor</h2>
            <p className="text-base leading-relaxed text-slate-300 mb-6">
              {settings.about_text || "Welcome to Slaynx! I'm a passionate content creator and developer dedicated to bringing you premium tutorials, coding content, and creative videos. With years of experience in video editing and content creation, I strive to deliver high-quality videos that inspire and educate."}
            </p>
            <p className="text-base leading-relaxed text-slate-300">
              Join me on this incredible journey as we explore the world of coding, editing, and creativity together.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section id="gallery" data-testid="gallery-section" className="py-24 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Showcase</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Gallery</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((item) => (
                <motion.div
                  key={item.id}
                  data-testid={`gallery-item-${item.id}`}
                  whileHover={{ scale: 1.05 }}
                  className="card-glass rounded-xl overflow-hidden group cursor-pointer"
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={item.image_url}
                      alt={item.title}
                      className="w-full aspect-video object-cover group-hover:scale-110"
                      style={{ transition: 'transform 0.3s' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.3s' }}>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-lg font-bold text-white">{item.title}</h3>
                        {item.description && <p className="text-sm text-slate-300">{item.description}</p>}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Downloads Section */}
      {downloads.length > 0 && (
        <section id="downloads" data-testid="downloads-section" className="py-24 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Resources</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Downloads</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {downloads.map((item) => (
                <motion.div
                  key={item.id}
                  data-testid={`download-item-${item.id}`}
                  whileHover={{ y: -5 }}
                  className="card-glass rounded-xl p-6 sm:p-8"
                >
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-300 mb-6 text-sm">{item.description}</p>
                  <a
                    href={item.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-cyan-500/50 text-cyan-400 font-bold px-6 py-3 rounded-md uppercase tracking-wide hover:bg-cyan-500/10"
                  >
                    Download
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      )}

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-24 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card-glass rounded-xl p-8 text-center">
              <div className="text-5xl font-black font-['Unbounded'] text-cyan-400 mb-2">
                <CountUp end={stats.total_videos} duration={2} />
              </div>
              <div className="text-slate-300 uppercase tracking-wide text-sm">Total Videos</div>
            </div>
            <div className="card-glass rounded-xl p-8 text-center">
              <div className="text-5xl font-black font-['Unbounded'] text-cyan-400 mb-2">
                <CountUp end={stats.total_views} duration={2} separator="," suffix="+" />
              </div>
              <div className="text-slate-300 uppercase tracking-wide text-sm">Total Views</div>
            </div>
            <div className="card-glass rounded-xl p-8 text-center">
              <div className="text-5xl font-black font-['Unbounded'] text-cyan-400 mb-2">
                <CountUp end={stats.subscriber_count} duration={2} separator="," suffix="+" />
              </div>
              <div className="text-slate-300 uppercase tracking-wide text-sm">Subscribers</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" data-testid="contact-section" className="py-24 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-cyan-400">Get in Touch</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Unbounded'] text-white mt-2">Contact</h2>
          </div>
          <div className="card-glass rounded-xl p-8 max-w-2xl mx-auto text-center">
            {settings.contact_email && (
              <div className="mb-6">
                <p className="text-slate-300 mb-2">Email:</p>
                <a href={`mailto:${settings.contact_email}`} className="text-xl text-cyan-400 hover:text-cyan-300 font-semibold">
                  {settings.contact_email}
                </a>
              </div>
            )}
            <div className="flex justify-center gap-4">
              {settings.youtube_url && (
                <a
                  data-testid="contact-youtube-link"
                  href={settings.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20"
                >
                  <YoutubeLogo size={28} weight="fill" className="text-cyan-400" />
                </a>
              )}
              {settings.instagram_url && (
                <a
                  data-testid="contact-instagram-link"
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20"
                >
                  <InstagramLogo size={28} weight="fill" className="text-cyan-400" />
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-cyan-500/10 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© 2024 Slaynx. All rights reserved.</p>
        </div>
      </footer>

      {/* Back to Top Button */}
      {showBackToTop && (
        <motion.button
          data-testid="back-to-top-button"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-cyan-500 text-black rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:bg-cyan-400 z-50"
        >
          <CaretUp size={24} weight="bold" />
        </motion.button>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <VideoPlayerModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
      )}
    </div>
  );
}

function VideoCard({ video, onPlay }) {
  const handleWatchNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPlay(video);
  };

  return (
    <motion.a
      data-testid={`video-card-${video.id}`}
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -5 }}
      className="card-glass rounded-xl overflow-hidden group cursor-pointer block"
    >
      <div className="relative overflow-hidden">
        <img 
          src={video.thumbnail_url || 'https://images.unsplash.com/photo-1550602883-4c2d2c705db2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsdWUlMjBuZW9uJTIwZ2xhc3N8ZW58MHx8fHwxNzgwNDA5OTg1fDA&ixlib=rb-4.0&q=85'}
          alt={video.title}
          className="w-full aspect-video object-cover group-hover:scale-110"
          style={{ transition: 'transform 0.3s' }}
          onError={(e) => {
            if (e.target.src.includes('maxresdefault')) {
              e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
            }
          }}
        />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center" style={{ transition: 'opacity 0.3s' }}>
          <Play size={48} weight="fill" className="text-cyan-400" />
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{video.title}</h3>
        <p className="text-sm text-slate-300 mb-3 line-clamp-2">{video.description}</p>
        <div className="flex items-center justify-between">
          <button
            data-testid={`watch-video-${video.id}`}
            onClick={handleWatchNow}
            className="inline-flex items-center gap-2 text-cyan-400 font-bold text-sm hover:text-cyan-300"
          >
            <Play size={16} weight="fill" />
            Watch Now
          </button>
          <span className="text-xs text-slate-400 inline-flex items-center gap-1">
            <Eye size={14} weight="duotone" />
            {formatViews(video.view_count)} views
          </span>
        </div>
      </div>
    </motion.a>
  );
}

function ShortsCard({ video, onPlay }) {
  const handleWatchHere = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPlay(video);
  };

  return (
    <motion.a
      data-testid={`shorts-card-${video.id}`}
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -5, scale: 1.02 }}
      className="card-glass rounded-xl overflow-hidden group relative block"
    >
      <div className="relative aspect-[9/16] overflow-hidden">
        <img 
          src={video.thumbnail_url || 'https://images.unsplash.com/photo-1550602883-4c2d2c705db2?crop=entropy&cs=srgb&fm=jpg&q=85'}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-110"
          style={{ transition: 'transform 0.3s' }}
          onError={(e) => {
            if (e.target.src.includes('maxresdefault')) {
              e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
            }
          }}
        />
        <div className="absolute top-2 right-2 bg-pink-500/90 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
          Shorts
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <button
          onClick={handleWatchHere}
          aria-label="Watch here"
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
          style={{ transition: 'opacity 0.3s' }}
        >
          <div className="w-16 h-16 rounded-full bg-cyan-500/90 flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.6)]">
            <Play size={32} weight="fill" className="text-black ml-1" />
          </div>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-3 pointer-events-none">
          <h3 className="text-sm font-bold text-white line-clamp-2">{video.title}</h3>
          <div className="text-xs text-cyan-300 inline-flex items-center gap-1 mt-1">
            <Eye size={12} weight="duotone" />
            {formatViews(video.view_count)}
          </div>
        </div>
      </div>
    </motion.a>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function VideoPlayerModal({ video, onClose }) {
  const videoId = extractYouTubeId(video.url);
  const isShort = video.is_short;

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="video-player-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className={`relative ${isShort ? 'w-full max-w-[400px]' : 'w-full max-w-5xl'}`}
      >
        <button
          data-testid="close-player-button"
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-cyan-400 z-10"
          aria-label="Close player"
        >
          <X size={32} weight="bold" />
        </button>
        <div className={`relative ${isShort ? 'aspect-[9/16]' : 'aspect-video'} rounded-xl overflow-hidden border border-cyan-500/30 shadow-[0_0_40px_rgba(0,240,255,0.3)]`}>
          {videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white">
              Invalid video URL
            </div>
          )}
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-xl font-bold text-white">{video.title}</h3>
          {video.description && <p className="text-sm text-slate-400 mt-2">{video.description}</p>}
        </div>
      </motion.div>
    </motion.div>
  );
}
